import { useQueryClient } from 'react-query';

import showError from 'components/shared/showError';
import { mutations, queryKeys } from 'RestAPIs/rapini';
import { toProtocolRequest } from '../ProtocolForm.utils';

import type { ProtocolResponse } from 'generated/index';
import type { ProtocolFormValues } from '../ProtocolForm.types';

/**
 * Creates the protocol, or updates it when one is given. Resolves once the list has been refetched,
 * so the caller can open the saved protocol straight from it.
 */
export const useSaveProtocol = (
  protocol: ProtocolResponse | undefined,
  onSaved: (protocol: ProtocolResponse) => void
) => {
  const queryClient = useQueryClient();
  const options = {
    onSuccess: async (saved: ProtocolResponse) => {
      await queryClient.invalidateQueries(queryKeys.getProtocols());
      onSaved(saved);
    },
    onError: showError
  };
  const { mutate: createProtocol, isLoading: isCreating } =
    mutations.useCreateProtocol(options);
  const { mutate: updateProtocol, isLoading: isUpdating } =
    mutations.useUpdateProtocol(protocol?.id ?? '', options);

  const saveProtocol = (values: ProtocolFormValues) => {
    const request = toProtocolRequest(values);
    if (protocol) {
      updateProtocol(request);
    } else {
      createProtocol(request);
    }
  };

  return { saveProtocol, isSaving: isCreating || isUpdating };
};
